// WorkspaceLanding — the workspace's AI-first home, pure.
//
// The live realisation of the owner-approved home-composer sketch. A centred
// spine on the ambient landing ground (`--color-bg-subtle`), flanked by light
// margins:
//
//   left  SLOT   = empty (a symmetric track that keeps the spine dead-centre)
//   center SPINE = orient → DO → were:
//                    · the ONE focal element: the PowerComposer (the SAME
//                      composer the chat uses, same look, G12), resting LOW
//                      in the spine — its `/`-slash and
//                      `@`-mention vocabulary replace the old bespoke "+" menu
//                    · recent conversations — a STABLE structure with an honest
//                      empty state (never absent, so the layout never jumps)
//   right ASIDE  = LEARN           → PAUSED (see SHOW_TIPS below)
//
// The composer is the single dominant focal point; everything else recedes to
// dim margins with no borders/cards. Both asides are gone for now — the former
// left resources aside was cut ("didn't add much"), and the right LEARN tip
// aside is PAUSED (owner call — more distracting than informative). Their empty
// tracks are kept so the spine stays centred in the free centre column (it
// shifts with the rail, like every other centre view — D10).
//
// Empty-state degradation is STRUCTURAL, not a flag (`LandingState` union): a
// brand-new workspace (`new`) carries no threads, so it degrades to the focal
// composer alone; a `returning` workspace carries the recent conversations
// (which render their own empty state when there are none).
//
// No workspace-wide activity here (#437): the landing is a chat's empty
// conversation, and a chat shows only its own conversation, never a recap of
// other projects' work. The semantic summary lives in the Journal.
//
// Pure/presentational: every datum arrives via props, every intent reports UP.
// No state, no I/O, no dates (recency labels arrive pre-formatted). Tip
// rotation, tip opt-out and autofocus-on-arrival all live in the impure host —
// the host focuses the composer's text
// field by querying its stable landing class at its own edge (a sanctioned
// DOM read), never from inside this pure tree.

import { exhaustSwitchCase } from "@bilda/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { ArcProminence } from "../../app/coaching.js";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { ComposerAttachButton } from "../Composer/PowerComposer/ComposerAttachButton.js";
import { PowerComposer } from "../Composer/PowerComposer/PowerComposer.js";
import type { PowerComposerStatus } from "../Composer/PowerComposer/sendState.js";
import type {
    ComposerResourceRef,
    ComposerSlashCommand,
} from "../Composer/PowerComposer/types.js";
import type { ContextItem, PendingUpload } from "../ContextCard/types.js";
import { mentionsToContextItems } from "../ContextCard/types.js";
import { Icon } from "../Icon/Icon.js";
import { OutcomeArc } from "../OutcomeArc/OutcomeArc.js";
import {
    derivePipelineArc,
    type PipelineProgress,
    type PipelineStepKind,
} from "./pipelineArc.js";
import "./WorkspaceLanding.css";

// === View-model — named shapes; every varying thing a discriminant ===========

// A capability tip, categorised by the verb it teaches. The category is a real
// discriminant: each renders its own lead-in word ("Try" / "Drop" / "Ask") and
// a new category breaks the fold below.
export type TryTip = { readonly kind: "try"; readonly text: string };
export type DropTip = { readonly kind: "drop"; readonly text: string };
export type AskTip = { readonly kind: "ask"; readonly text: string };
export type CapabilityTip = TryTip | DropTip | AskTip;

// One recent CONVERSATION row: id to route back, title to recognise, `when` a
// pre-formatted relative label (this component never touches Date).
// `lastActivityAt` is the RAW instant behind that label (ISO 8601 UTC) — the
// Home overview projection reads it directly for freshness + a true global
// newest-first sort (a formatted label can't be compared or aged); absent when
// the thread has no recorded activity yet, which degrades to no-date/stale.
export type RecentThread = {
    readonly id: string;
    readonly title: string;
    readonly when: string;
    readonly lastActivityAt?: string;
    readonly archived?: boolean;
    // The project it belongs to (#404); absent: none.
    readonly projectId?: string;
};

// Empty-state degradation as STRUCTURE, not a flag: `new` carries nothing;
// `returning` carries the recent conversations (which may still be empty and
// render their own honest empty state). The focal composer renders for both.
export type NewWorkspaceState = { readonly type: "new" };
export type ReturningWorkspaceState = {
    readonly type: "returning";
    // Recent conversations, most-recent first. Empty renders the empty state.
    readonly threads: readonly RecentThread[];
};
export type LandingState = NewWorkspaceState | ReturningWorkspaceState;

export type WorkspaceLandingProps = {
    readonly state: LandingState;
    /** Optional device-local author name; absent names degrade gracefully. */
    readonly displayName?: string;
    // The hero composer's draft — owned by the host, reported back on change.
    // This is the SAME shared draft the chat composer edits (the host holds one
    // draft cell), so a line typed here survives the jump into the conversation.
    readonly draft: string;
    readonly onDraftChange: (value: string) => void;
    // Send: routes the draft through the real turn path (the host opens the
    // conversation). Named for the DESIGN intent ("start a thread"); the
    // single-conversation reality is the host's concern, not this view's.
    // Wired to PowerComposer's `onSend`.
    readonly onStartThread: () => void;
    // The hero composer is the SAME PowerComposer the chat uses (G12: same
    // look) — so the whole send-state machine + `@`-mention + `/`-slash
    // vocabulary is forwarded straight through from the host (the exact wiring
    // the chat already feeds), never a parallel path. Each is its own named
    // type; distinct intents (send / stop / retry) stay distinct callbacks.
    readonly composerStatus: PowerComposerStatus;
    readonly onRetry: () => void;
    // The resources `@`-mentioned into the draft so far — controlled, so the
    // host reads the exact set to ground the turn. Replaces the old bespoke
    // "Insert a reference" menu item.
    readonly mentions: readonly ComposerResourceRef[];
    readonly onMentionsChange: (
        mentions: readonly ComposerResourceRef[],
    ) => void;
    // The `@`-mentionable resource pool and the `/`-slash quick actions —
    // data-driven, the same live lists the chat composer offers.
    readonly composerCandidates: readonly ComposerResourceRef[];
    readonly composerCommands: readonly ComposerSlashCommand[];
    // Opens the file picker to add a source. Surfaced as the ContextCard's
    // "Upload a file" entrance (the card SUBSUMES the former standalone "Add a
    // source" button, REQ-UI-001 — one front door, not two): visible, labelled,
    // keyboard-reachable, and it opens the same ingest picker the host wires
    // here.
    readonly onAddSource: () => void;
    // In-flight uploads still being ingested — a TRANSIENT UI state the card
    // shows alongside the grounded mentions until each resolves to a `source`
    // (Q3: no 4th resource kind). Absent ⇒ none in flight. The host owns the
    // ingest lifecycle and drives this; the card only renders it.
    readonly contextUploads?: readonly PendingUpload[];
    // The CURRENT-TASK authoring progress (F-D / DB2), computed by the host from
    // task-scoped signals (this conversation's grounding + transcript) — NOT the
    // whole workspace inventory. Drives the OutcomeArc's node states.
    readonly pipelineProgress: PipelineProgress;
    // How loudly the arc presents (Dm4) — derived upstream from the author's
    // experience ladder, never a raw toggle. Default `prominent`.
    readonly arcProminence?: ArcProminence;
    // "Take a guided tour" (D28): starts the spotlight tour CoachHost owns,
    // for the view beside the chat; null when that view has none.
    readonly onStartTour: (() => void) | null;
    // The single tip on show, or null when tips are opted out / exhausted.
    // (Currently unused at render — the tip aside is PAUSED; see SHOW_TIPS.)
    readonly tip: CapabilityTip | null;
    // The opt-out — "don't show tips". Absent → the dismiss control hides.
    readonly onDismissTips?: () => void;
    // Jump straight back into a prior conversation.
    readonly onOpenThread?: (thread: RecentThread) => void;
    // "See all" conversations — conceptually the activity rail.
    readonly onSeeAllThreads?: () => void;
    // The first-message TRANSITION (owner: "liten, snabb"): `resting` is the
    // ordinary hero; `exiting` is the ONE-beat hold the host renders after the
    // conversation already has a message, so the composer's glide + the
    // caption/CTA fade (see the CSS) can play before the shell swaps to the
    // transcript. Absent ⇒ `resting` — every other caller of this component is
    // unaffected. `onExitTransitionEnd` fires once the exit's own CSS
    // transition completes (a real DOM `transitionend`, not a guessed
    // duration) — the host uses it to know when it may finish the swap.
    readonly phase?: "resting" | "exiting";
    readonly onExitTransitionEnd?: () => void;
    // NOTE (owner-approved landing redesign): the former center ContextCard
    // grounding strip (`showContextStrip`) is GONE — the composer's own
    // inline `@`-mention chips (contextDisplay="inline" below) and the "+"
    // attach menu are now the ONE in-spine grounding surface. The
    // ContextSideCard riding on the RIGHT (host-owned, outside this
    // component) still separately owns audience/situation grounding — out of
    // scope for this redesign (.claude/analysis/context-panel-unification).
    readonly className?: string;
};

// How many recent conversations show at rest — the rest fold into a "See all".
const THREADS_SHOWN = 3;

// Tips aside PAUSED (owner call, 2026-08-19): the rotating capability tips read
// as more distracting than informative right now, so the right "learn" aside is
// not rendered. Code + types (`TipAside`, `CapabilityTip`, the `tip` /
// `onDismissTips` props) are kept intact — flip this to `true` to restore it.
const SHOW_TIPS = false;

// The authoring step-indicator (Source → Ask → Material → Package) and the
// "Pick up a conversation" resume list are PAUSED (owner-approved landing
// redesign, ratified findings F2/F6): the composer is now the unambiguous
// hero and both read as competing chrome around it. Same pattern as
// SHOW_TIPS above — code + props (`pipelineProgress`, `arcProminence`,
// `onOpenThread`, `onSeeAllThreads`, the `returning` state's `threads`) stay
// intact; flip either flag to restore.
const SHOW_PIPELINE_ARC = false;
const SHOW_RECENT_CONVERSATIONS = false;

// === Totals over the sums ====================================================

// The tip's lead-in word — a new tip category breaks compilation here.
const tipLead = (kind: CapabilityTip["kind"]): ReactNode => {
    switch (kind) {
        case "try":
            return <Trans>Try</Trans>;
        case "drop":
            return <Trans>Drop</Trans>;
        case "ask":
            return <Trans>Ask</Trans>;
        default:
            return exhaustSwitchCase(kind);
    }
};

// === Component ===============================================================

export const WorkspaceLanding = ({
    state,
    displayName,
    draft,
    onDraftChange,
    onStartThread,
    composerStatus,
    onRetry,
    mentions,
    onMentionsChange,
    composerCandidates,
    composerCommands,
    onAddSource,
    contextUploads = [],
    pipelineProgress,
    arcProminence = { type: "prominent" },
    onStartTour,
    tip,
    onDismissTips,
    onOpenThread,
    onSeeAllThreads,
    phase = "resting",
    onExitTransitionEnd,
    className,
}: WorkspaceLandingProps): ReactNode => {
    const { t } = useLingui();
    // The card and the composer's `@`-mention ground the SAME set: the card's
    // items are the mentions projected to resolved-resource items, plus any
    // in-flight uploads (transient, no resource id yet). Picking/removing in
    // the card edits the SAME controlled `mentions` list the `@` menu commits
    // to — one command, two entrances.
    const contextItems: readonly ContextItem[] = [
        ...mentionsToContextItems(mentions),
        ...contextUploads.map(
            (upload): ContextItem => ({ _tag: "upload", upload }),
        ),
    ];
    const root = `bilda-workspace-landing${className ? ` ${className}` : ""}`;
    const threads = state.type === "returning" ? state.threads : [];
    const name = displayName?.trim();
    // The CTA list below the composer (owner-approved landing redesign) —
    // help/action framed, re-aimed toward what the assistant can DO rather
    // than a content-type taxonomy. Each row prefills the SAME draft the
    // composer edits (real `onDraftChange`, no invented command) — the
    // author reviews/edits, then sends through the real turn pipeline.
    const quickActions = [
        {
            id: "help",
            label: t`Help me`,
            promptText: t`Help me figure out what to create from my materials.`,
        },
        {
            id: "torus_course",
            label: t`Create a Torus course`,
            promptText: t`Create a Torus course from my material.`,
        },
        {
            id: "quiz",
            label: t`Create a quiz`,
            promptText: t`Create a quiz from my material.`,
        },
    ] as const;

    // The "+" attach menu on the hero composer (owner-approved landing
    // redesign): exactly two doors, matching the approved design — upload a
    // NEW source (the real ingest picker) or choose an EXISTING one (a
    // nested pick list, the SAME candidates/dedup vocabulary the composer's
    // `@`-mention and the former ContextCard already shared via
    // `pickMenuItems`). No third, unadvertised `@`-mention item here — `@`
    // stays a discoverable in-composer convenience, per the approved design.
    // The trigger + menu themselves live in `ComposerAttachButton` — the ONE
    // shared component the ongoing-chat composer's leadingSlot also renders
    // (F10 parity), so the two composers can never visually drift apart.
    const pickSource = (source: ComposerResourceRef) =>
        onMentionsChange(
            mentions.some((m) => m.id === source.id)
                ? mentions
                : [...mentions, source],
        );

    // The authoring step-indicator (Source → Ask → Material → Package). Its
    // node states fold over the CURRENT TASK's progress (F-D / DB2) — supplied
    // by the host from task-scoped signals, never the whole workspace library —
    // so a returning workspace no longer lights three greens on a fresh task.
    // The label + per-step explanation are real localised copy; the derivation
    // itself stays locale-free.
    const arcLabel = (kind: PipelineStepKind): string => {
        switch (kind) {
            case "source":
                return t`Source`;
            case "ask":
                return t`Ask`;
            case "material":
                return t`Material`;
            case "package":
                return t({ message: "Package", context: "resource kind" });
            default:
                return exhaustSwitchCase(kind);
        }
    };
    // On-demand teaching copy (Dm4): what each step means, revealed on
    // focus/hover so the structure stays calm and the explanation is opt-in.
    const arcExplanation = (kind: PipelineStepKind): string => {
        switch (kind) {
            case "source":
                return t`Add the material you want to build from — a PDF or an existing source.`;
            case "ask":
                return t`Describe what to create; the assistant drafts it from your source.`;
            case "material":
                return t`Review and refine the generated material until it's right.`;
            case "package":
                return t`Export the finished material for Torus, Canvas or Markdown.`;
            default:
                return exhaustSwitchCase(kind);
        }
    };
    const arcSteps = derivePipelineArc(
        pipelineProgress,
        arcLabel,
        arcExplanation,
    );

    return (
        <div className={root}>
            {/* The panel's empty conversation (redesign B-02): the assistant
                opens at the foot, just above the composer — who speaks (with
                the AI marker), a greeting, a few starting
                points — and the composer sits in the card's foot. */}
            <section
                className={`bilda-workspace-landing__hero${
                    phase === "exiting"
                        ? " bilda-workspace-landing__hero--exiting"
                        : ""
                }`}
            >
                <div className="bilda-workspace-landing__intro">
                    {/* The authoring step-indicator: PAUSED (SHOW_PIPELINE_ARC). */}
                    {SHOW_PIPELINE_ARC ? (
                        <div className="bilda-workspace-landing__arc">
                            <OutcomeArc
                                steps={arcSteps}
                                ariaLabel={t`Your authoring journey`}
                                prominence={arcProminence}
                            />
                        </div>
                    ) : null}
                    {/* Recent conversations: PAUSED (the sidebar and the
                        picker list them). */}
                    {SHOW_RECENT_CONVERSATIONS ? (
                        <RecentConversations
                            threads={threads}
                            onOpenThread={onOpenThread}
                            onSeeAllThreads={onSeeAllThreads}
                        />
                    ) : null}
                    <div className="bilda-workspace-landing__who">
                        <span>
                            {t({
                                message: "Assistant",
                                context: "chat speaker",
                            })}
                        </span>
                        <AiMarker variant="tag" />
                    </div>
                    <p className="bilda-workspace-landing__welcome">
                        {name !== undefined && name !== ""
                            ? t`Hi, ${name}!`
                            : t`Hi!`}{" "}
                        {t`What shall we make? Describe what you need, or add sources, and I'll draft a proposal.`}
                    </p>
                    {/* Starting points: each fills the SAME draft the composer
                        edits; the author reviews it, then sends. */}
                    {/* biome-ignore lint/a11y/useSemanticElements: role="group" names a labelled cluster of non-form buttons; <fieldset> is a form idiom (the CommandPalette precedent). */}
                    <div
                        className="bilda-workspace-landing__quickList"
                        role="group"
                        aria-label={t`Quick starts`}
                    >
                        {quickActions.map((qa) => (
                            <button
                                key={qa.id}
                                type="button"
                                className="bilda-workspace-landing__quickRow"
                                onClick={() => onDraftChange(qa.promptText)}
                            >
                                <span className="bilda-workspace-landing__quickRowLabel">
                                    {qa.label}
                                </span>
                            </button>
                        ))}
                    </div>
                    {/* D28: one quiet way into the guided tour of the view
                        beside it (never auto-started, Dm9); none there, no
                        link. */}
                    {onStartTour === null ? null : (
                        <button
                            type="button"
                            className="bilda-workspace-landing__tourLink"
                            onClick={onStartTour}
                        >
                            <Icon name="play" size="xs" />
                            <Trans>Take a guided tour</Trans>
                        </button>
                    )}
                </div>

                {/* The SAME PowerComposer the chat renders (G12); its menus
                    open above (it sits at the foot). The landing class is the
                    host's autofocus target. */}
                <div
                    className="bilda-workspace-landing__composerWrap"
                    onTransitionEnd={(e) => {
                        // The glide's own transitionend, filtered to fire once
                        // (opacity, not transform, and not a bubbled one).
                        if (
                            phase === "exiting" &&
                            e.propertyName === "opacity" &&
                            e.currentTarget === e.target
                        )
                            onExitTransitionEnd?.();
                    }}
                >
                    <PowerComposer
                        className="bilda-workspace-landing__composer"
                        menuPlacement="above"
                        conversationPhase="new"
                        leadingSlot={
                            <ComposerAttachButton
                                candidates={composerCandidates}
                                contextItems={contextItems}
                                onUpload={onAddSource}
                                onPick={pickSource}
                            />
                        }
                        value={draft}
                        onValueChange={onDraftChange}
                        onSend={onStartThread}
                        status={composerStatus}
                        onRetry={onRetry}
                        mentions={mentions}
                        onMentionsChange={onMentionsChange}
                        candidates={composerCandidates}
                        commands={composerCommands}
                    />
                </div>
            </section>
            {/* The tips aside: PAUSED (SHOW_TIPS). */}
            {SHOW_TIPS && tip !== null ? (
                <TipAside tip={tip} onDismiss={onDismissTips} />
            ) : null}
        </div>
    );
};

// were: the demoted return layer. A STABLE section — the label always shows;
// the body is either a few recent conversations + a quiet "See all" tail
// (summary-first) or an honest empty state when there are none.
const RecentConversations = ({
    threads,
    onOpenThread,
    onSeeAllThreads,
}: {
    readonly threads: readonly RecentThread[];
    readonly onOpenThread?: (thread: RecentThread) => void;
    readonly onSeeAllThreads?: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const shown = threads.slice(0, THREADS_SHOWN);
    return (
        <div className="bilda-workspace-landing__recents">
            <span className="bilda-workspace-landing__recentsLabel">
                <Trans>Pick up a conversation</Trans>
            </span>
            {threads.length === 0 ? (
                <p className="bilda-workspace-landing__recentsEmpty">
                    <Trans>No conversations yet — start by typing above.</Trans>
                </p>
            ) : (
                <>
                    <ul className="bilda-workspace-landing__threadList">
                        {shown.map((thread) => (
                            <li key={thread.id}>
                                <button
                                    type="button"
                                    className="bilda-workspace-landing__threadRow"
                                    onClick={() => onOpenThread?.(thread)}
                                >
                                    <Icon name="chat" size="sm" />
                                    <span className="bilda-workspace-landing__threadName">
                                        {thread.title || t`New conversation`}
                                    </span>
                                    <span className="bilda-workspace-landing__threadWhen">
                                        {thread.when}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    <button
                        type="button"
                        className="bilda-workspace-landing__seeAll"
                        onClick={onSeeAllThreads}
                    >
                        <Trans>See all conversations</Trans>
                        <Icon name="arrow-right" size="sm" />
                    </button>
                </>
            )}
        </div>
    );
};

// RIGHT ASIDE — LEARN. One tip at a time, loading-screen register: a lead-in
// verb + the affordance, with a whisper-weight opt-out. PAUSED at render (see
// SHOW_TIPS) but kept intact for a one-line restore.
const TipAside = ({
    tip,
    onDismiss,
}: {
    readonly tip: CapabilityTip;
    readonly onDismiss?: () => void;
}): ReactNode => (
    <aside className="bilda-workspace-landing__aside bilda-workspace-landing__aside--right">
        <span className="bilda-workspace-landing__asideLabel">
            <Trans>Did you know</Trans>
        </span>
        <p className="bilda-workspace-landing__tip">
            <span className="bilda-workspace-landing__tipLead">
                {tipLead(tip.kind)}
            </span>
            <span className="bilda-workspace-landing__tipText">{tip.text}</span>
        </p>
        {onDismiss !== undefined ? (
            <button
                type="button"
                className="bilda-workspace-landing__asideMore"
                onClick={onDismiss}
            >
                <Trans>Don't show tips</Trans>
            </button>
        ) : null}
    </aside>
);
